import BaseChart from './BaseChart'
import ItemsHelper from '../chart_helpers/ItemsHelper'
import RootHelper from '../chart_helpers/RootHelper'
import AxisHelper from '../chart_helpers/AxisHelper'
import SizeHelper from '../chart_helpers/SizeHelper'
import { categoricalExtent, continuousExtent } from '../chart_helpers/AxisHelper'
import SelectableHelper from '../chart_helpers/SelectableHelper'
import { getConnectionLines } from '../viz_behaviors/ConnectedDotPlotDataController'
import {
  drawSvgCircle,
  drawSvgPlus,
  drawSvgPolygon,
  drawSvgSquare,
  drawSvgStar,
  drawSvgTriangle,
} from '../utils/shapeUtils'
import * as COLORS from '../utils/colorUtils'

export default class ConnectedDotPlot extends BaseChart {
  constructor(...args) {
    super(...args)
    this.zeroBased = true
  }
  static defaultConfig = {
    isPortrait: {
      type: Boolean,
      default: true,
      exposed: false,
      nonEditable: true,
    },
    showDatumValues: {
      type: Boolean,
      default: false,
      exposed: true,
    },
    /**
     * {-EN-} Define the data point size that form each line.
     * {-ES-} Define el tamaño los puntos de datos que forman cada línea.
     */
    pointsSize: {
      type: [Number, String],
      default: 3,
      options: ['hidden', 3, 6, 9],
      exposed: true,
    },
    xAttr: {
      type: String,
      default: 'value',
      exposed: false,
    },
    yAttr: {
      type: String,
      default: 'id',
      exposed: false,
    },
    AxisHelper: {
      zoom: {
        nonEditable: true,
      },
      scroll: {
        default: false,
        exposed: true,
      },
      showScrollbar: {
        default: true,
        exposed: true,
      },
      y: {
        scale: {
          value: 'point',
          exposed: false,
          nonEditable: true,
        },
        showTickValues: {
          value: true,
          exposed: false,
          nonEditable: true,
        },
        tickOrientation: {
          exposed: false,
          nonEditable: true,
        },
        grid: true,
        unit: {
          exposed: false,
          nonEditable: true,
        },
        trimThreshold: {
          value: false,
          exposed: false,
          nonEditable: true,
        },
        tickWrap: {
          exposed: true,
          nonEditable: false,
        },
        getExtent: function (data) {
          return categoricalExtent(data, d => d.id)
        },
      },
      x: {
        showTickValues: {
          value: true,
          exposed: false,
          nonEditable: true,
        },
        scale: {
          value: 'linear',
          exposed: false,
          nonEditable: true,
        },
        tickOrientation: {
          exposed: true,
          nonEditable: false,
        },
        trimThreshold: {
          value: false,
          exposed: false,
          nonEditable: true,
        },
        grid: false,
        getExtent: function (data) {
          const extent = continuousExtent(data, d => d[this.config.xAttr])
          return extent
        },
      },
    },
    TooltipBehavior: {
      getItemOffset: function ($el) {
        let original = $el.getBoundingClientRect()
        return {
          x: original.x + original.width / 2,
          y: original.top, //- 5,
        }
      },
      getHtml: function (d) {
        return `
          <div class="tip-value important">${this.formatTooltipValue(d.value, 'x')}</div>
          <div class="tip-id">
            <span class="icon" style="display: inline-block; width: 9px; height: 9px; border-radius: 50%; background: ${this.color(
              { id: d.parentId }
            )}; margin-right: 3px"></span>
            ${d.parentId}
          </div>
          <div class="secondary">${this.formatValue(d.id, 'y')}</div>
            `
      },
    },
  }
  static helpers = [RootHelper, ItemsHelper, SelectableHelper, SizeHelper, AxisHelper]
  checkConsistency() {
    return true
  }
  beforeHelpers() {
    this.margin = { top: 20, right: 40, left: 20, bottom: 20 }
  }
  draw() {
    // NO ESTAN BIEN ESTAS "druation=1000" hardcoded,
    //deberian usarse los "itemsTrans" y "parentsItemsTrans" que YA LLEVAN LA DURATION QUE TOCA...

    //Draw lines between points
    const connectionLines = getConnectionLines(this.ghostData || this.data)

    let lines = this.content
      .selectAll('.connection-line')
      .data(Object.values(connectionLines), d => d.id)
    const linesEnter = lines
      .enter()
      .append('line')
      .attr('class', 'connection-line')
      .attr('stroke', d => '#E3E3E3')
      .attr('stroke-width', 2)
      .attr('x1', d => {
        return this.axes.x.func.scale()(d.xConnectStart)
      })
      .attr('x2', d => {
        return this.axes.x.func.scale()(d.xConnectStart)
      })
      .lower()

    lines.exit().transition().duration(this.getDuration()).style('opacity', 0).remove()
    lines = lines.merge(linesEnter)
    lines
      .attr('x1', d => {
        return this.axes.x.func.scale()(d.xConnectStart)
      })
      .attr('y1', d => this.axes.y.func.scale()(d[this.config.yAttr]))
      .attr('y2', d => this.axes.y.func.scale()(d[this.config.yAttr]))
      .style('opacity', 1)
      .transition()
      .duration(this.getDuration())
      .delay(this.getDuration() / 2)
      .attr('x2', d => {
        return this.axes.x.func.scale()(d.xConnectEnd)
      })

    //Draw points
    let items = this.items.merge(this.itemsEnter)
    let breakPoints = items.selectAll('.break-point').data(d => [d])
    const breakPointsEnter = breakPoints
      .enter()
      .append('g')
      .attr('class', 'break-point')
      .attr(
        'transform',
        d =>
          `translate(${this.axes.x.func.scale()(d[this.config.xAttr])},${this.axes.y.func.scale()(
            d[this.config.yAttr]
          )})`
      )
      .attr('stroke', d => this.color({ id: d.parentId }))
    breakPoints = breakPoints.merge(breakPointsEnter)
    breakPoints.exit().remove()

    const getPointType = d => {
      if (
        !this.config.ItemsHelper.colorDict ||
        !this.config.ItemsHelper.colorDict[d.parentId]?.shape
      )
        return 'circle'
      return this.config.ItemsHelper.colorDict[d.parentId].shape
    }

    const drawShapes = (className, shapeType, drawEnterCb, drawMergeCb) => {
      let shapes = breakPoints.selectAll(`.${className}`).data(
        d => (getPointType(d) === shapeType ? [d] : []),
        d => d.parentId
      )

      const shapesEnter = shapes
        .enter()
        .append('g')
        .attr('class', `point-shape ${className}`)
        .attr('stroke-width', 0)
      shapesEnter.call(drawEnterCb)
      shapes.exit().transition().duration(800).style('opacity', 0).remove()
      shapes = shapes.merge(shapesEnter)
      shapes.attr('fill', d => this.color({ id: d.parentId }))
      this.itemsEnterTrans
        .select('.break-point')
        .attr('fill', d => this.color({ id: d.parentId }))
        .attr(
          'transform',
          d =>
            `translate(${this.axes.x.func.scale()(d[this.config.xAttr])},${this.axes.y.func.scale()(
              d[this.config.yAttr]
            )})`
        )
        .select(`.${className}`)
        .call(drawMergeCb)

      this.itemsTrans
        .select('.break-point')
        .attr('fill', d => this.color({ id: d.parentId }))
        .attr('stroke', d => this.color({ id: d.parentId }))
        .attr(
          'transform',
          d =>
            `translate(${this.axes.x.func.scale()(d[this.config.xAttr])},${this.axes.y.func.scale()(
              d[this.config.yAttr]
            )})`
        )
        .select(`.${className}`)
        .call(drawMergeCb)

      this.itemsExitTrans.select('.break-point').select(`.${className}`).attr('opacity', 0)
    }
    const SHAPES_DEFAULT_SIZE = this.config.pointsSize
      ? this.config.pointsSize === 'hidden'
        ? 0
        : this.config.pointsSize
      : 3

    //Circle
    drawShapes(
      'circle',
      'circle',
      sel => drawSvgCircle(sel, 0, 'enter'),
      sel => {
        return drawSvgCircle(sel, d => SHAPES_DEFAULT_SIZE, 'edit')
      }
    )

    //Square
    drawShapes(
      'square',
      'square',
      sel => drawSvgSquare(sel, 0, 'enter'),
      sel => drawSvgSquare(sel, d => SHAPES_DEFAULT_SIZE * 2, 'edit')
    )
    //Triangles
    drawShapes(
      'triangle',
      'triangle',
      sel => drawSvgTriangle(sel, 0, 'enter'),
      sel => drawSvgTriangle(sel, d => SHAPES_DEFAULT_SIZE * 2, 'edit')
    )

    //Five point star
    drawShapes(
      'five-point-star',
      'fivePointStar',
      sel => drawSvgStar(sel, 0, 5, 'enter'),
      sel => drawSvgStar(sel, d => SHAPES_DEFAULT_SIZE * 2, 5, 'edit')
    )

    //Six point star
    drawShapes(
      'six-point-star',
      'sixPointStar',
      sel => drawSvgStar(sel, 0, 6, 'enter'),
      sel => drawSvgStar(sel, d => SHAPES_DEFAULT_SIZE * 2, 6, 'edit')
    )

    //Hexagon
    drawShapes(
      'hexagon',
      'hexagon',
      sel => drawSvgPolygon(sel, 0, 6, 'enter'),
      sel => drawSvgPolygon(sel, d => SHAPES_DEFAULT_SIZE * 2, 6, 'edit')
    )

    //Pentagon
    drawShapes(
      'pentagon',
      'pentagon',
      sel => drawSvgPolygon(sel, 0, 5, 'enter'),
      sel => drawSvgPolygon(sel, SHAPES_DEFAULT_SIZE * 2, 5, 'edit')
    )

    //Plus
    drawShapes(
      'plus',
      'plus',
      sel => drawSvgPlus(sel, 0, 'enter'),
      sel => drawSvgPlus(sel, SHAPES_DEFAULT_SIZE * 2, 'edit')
    )
  }
}
